<template>
  <div class="server-log-viewer">
    <el-alert
      title="查看当前服务器的应用日志和同步文件项目日志；选择历史日期可查询归档日志。"
      type="info"
      :closable="false"
      show-icon
    />

    <el-card class="viewer-card" shadow="never">
      <div class="toolbar">
        <el-select
          v-model="query.fileKey"
          placeholder="请选择日志类型"
          size="small"
          class="file-select"
          :disabled="loading"
        >
          <el-option
            v-for="file in files"
            :key="file.key"
            :label="file.label"
            :value="file.key"
          />
        </el-select>

        <el-input
          v-model="query.keyword"
          placeholder="关键字（普通文本匹配）"
          maxlength="100"
          clearable
          size="small"
          class="keyword-input"
          :disabled="loading"
          @keyup.enter.native="readLogs"
        />

        <el-select
          v-model="query.lines"
          size="small"
          class="line-select"
          :disabled="loading"
        >
          <el-option
            v-for="count in [200, 500, 1000]"
            :key="count"
            :value="count"
            :label="`${query.archiveDate || query.keyword.trim() ? '每段' : '最近'} ${count} 行`"
          />
        </el-select>

        <el-date-picker
          v-model="query.archiveDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="历史归档日期（可选）"
          clearable
          size="small"
          class="archive-date-picker"
          :disabled="loading"
        />

        <el-button
          type="primary"
          size="small"
          :loading="loading"
          @click="readLogs"
        >
          查询日志
        </el-button>
        <el-button size="small" :disabled="loading" @click="loadFiles">
          检查日志文件
        </el-button>
        <el-button
          v-if="result && result.hasMore && result.nextCursor"
          size="small"
          :disabled="loading"
          @click="continueLogs"
        >
          继续查询
        </el-button>
      </div>

      <el-alert
        v-if="selectedFile && selectedFile.locationChecks && selectedFile.locationChecks.length"
        class="file-check"
        :title="selectedFile.readable ? `当前可读取：${selectedFile.sourceLabel}` : '当前服务器未找到可读的日志文件'"
        :description="selectedFile.locationChecks.join('；')"
        :type="selectedFile.readable ? 'success' : 'warning'"
        :closable="false"
        show-icon
      />
      <el-alert
        v-if="fileCheckError"
        class="file-check"
        title="日志文件检查失败"
        :description="fileCheckError"
        type="error"
        :closable="false"
        show-icon
      />

      <div v-if="result" class="result-summary">
        <span>
          {{ result.label }}：已找到 {{ matchedLines }} 行，当前显示
          {{ result.returnedLines }} 行
        </span>
        <span v-if="result.archiveDate">
          历史归档：{{ result.archiveDate }}
        </span>
        <span v-if="result.readWindowLimited" class="limit-tip">
          为保护服务器性能，仅扫描文件末尾 2 MB。
        </span>
        <span v-if="result.archiveDate && result.hasMore" class="limit-tip">
          历史归档尚未查完，请点击“继续查询”。
        </span>
        <span v-if="!result.archiveDate && result.hasMore" class="limit-tip">
          当前日志尚未查完，请点击“继续查询”。
        </span>
        <span v-if="!result.archiveDate && lastQuery && lastQuery.keyword.trim() && !result.hasMore && !result.readWindowLimited && !readFailed">
          查询开始时的当前日志已查完。
        </span>
        <span v-if="result.archiveDate && !result.hasMore && !readFailed">
          历史归档已查完。
        </span>
        <span v-if="matchedLines > result.returnedLines" class="limit-tip">
          更早的匹配行已移出显示，当前保留最近的结果。
        </span>
      </div>
      <el-alert
        v-if="readFailed"
        title="本次查询未完成，请重新查询；已找到的日志仍保留，不能据此判断没有记录。"
        :description="readError"
        type="error"
        :closable="false"
        show-icon
      />

      <!-- v-text 只插入文本节点，日志中的 HTML 或脚本不会被浏览器执行。 -->
      <pre
        v-if="result && result.lines.length"
        class="log-content"
        v-text="logText"
      ></pre>
      <el-empty v-else-if="!loading" :description="emptyMessage" />
    </el-card>
  </div>
</template>

<script>
import { getServerLogFiles, readServerLog } from "@/api/serverLog";

// 续查累积结果，同时限制页面保留的行数和文字总量，避免长时间查询耗尽浏览器内存。
const MAX_DISPLAY_LINES = 5000;
const MAX_DISPLAY_CHARS = 4 * 1024 * 1024;

export default {
  name: "ServerLogViewer",
  data() {
    return {
      files: [],
      loading: false,
      query: {
        fileKey: "",
        keyword: "",
        lines: 200,
        archiveDate: "",
      },
      result: null,
      lastQuery: null,
      matchedLines: 0,
      readFailed: false,
      readError: "",
      fileCheckError: "",
    };
  },
  computed: {
    selectedFile() {
      return this.files.find((file) => file.key === this.query.fileKey) || null;
    },
    logText() {
      return this.result && this.result.lines
        ? this.result.lines.join("\n")
        : "";
    },
    emptyMessage() {
      if (this.readFailed) return "查询未完成，请重新查询";
      if (!this.result) return "请选择日志类型后查询";
      if (this.result.hasMore) return "当前段没有匹配日志，请继续查询";
      if (!this.result.archiveDate && this.lastQuery && this.lastQuery.keyword.trim() && !this.result.readWindowLimited) {
        return "查询开始时的当前日志已查完，没有匹配内容";
      }
      return this.result.archiveDate
        ? "该日期归档已查完，没有匹配日志"
        : "当前日志窗口内没有匹配内容";
    },
  },
  watch: {
    query: {
      deep: true,
      handler() {
        // 变更条件后必须开始新查询，不能把旧日期或旧关键词的结果拼到一起。
        if (!this.loading) {
          this.result = null;
          this.lastQuery = null;
          this.matchedLines = 0;
          this.readFailed = false;
          this.readError = "";
        }
      },
    },
  },
  created() {
    this.loadFiles();
  },
  methods: {
    loadFiles() {
      this.loading = true;
      this.fileCheckError = "";
      return getServerLogFiles()
        .then((response) => {
          this.files = response.data || [];
          if (!this.query.fileKey && this.files.length) {
            this.query.fileKey = this.files[0].key;
          }
        })
        .catch((error) => {
          this.fileCheckError = error && error.message ? error.message : "日志文件检查请求失败";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    readLogs() {
      return this.fetchLogs(false);
    },
    continueLogs() {
      if (!this.result || !this.result.nextCursor || !this.lastQuery) return;
      return this.fetchLogs(true);
    },
    fetchLogs(append) {
      if (this.loading) return;
      if (!this.query.fileKey) {
        this.$message.warning("当前服务器没有可查看的日志类型");
        return;
      }

      const request = append
        ? { ...this.lastQuery, cursor: this.result.nextCursor }
        : { ...this.query };
      this.loading = true;
      this.readFailed = false;
      this.readError = "";
      if (!append) {
        this.result = null;
        this.matchedLines = 0;
        this.lastQuery = { ...this.query };
      }
      return readServerLog(request)
        .then((response) => {
          const data = response.data;
          if (!data) return;
          const lines =
            append && this.result
              ? this.result.lines.concat(data.lines)
              : data.lines.slice();
          this.matchedLines += data.lines.length;
          let characters = lines.reduce(
            (total, line) => total + line.length,
            0
          );
          let start = 0;
          while (
            lines.length - start > MAX_DISPLAY_LINES ||
            characters > MAX_DISPLAY_CHARS
          ) {
            characters -= lines[start].length;
            start++;
          }
          this.result = {
            ...data,
            lines: lines.slice(start),
            returnedLines: lines.length - start,
          };
        })
        .catch((error) => {
          // 页面保留具体失败原因，避免统一错误弹窗消失后只剩“未查完”的泛化提示。
          this.readFailed = true;
          this.readError = error && error.message ? error.message : "日志读取请求失败";
          if (this.result) {
            this.result = { ...this.result, hasMore: false, nextCursor: null };
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.server-log-viewer {
  height: 100%;
  padding: 2px 4px 16px 0;
  box-sizing: border-box;
  overflow-y: auto;
}

.viewer-card {
  margin-top: 16px;
}

.file-check {
  margin-top: 12px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.file-select {
  width: 290px;
}

.line-select {
  width: 180px;
}

.keyword-input {
  width: 260px;
}

.archive-date-picker {
  width: 190px;
}

.result-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0 8px;
  color: #606266;
  font-size: 13px;
}

.limit-tip {
  color: #e6a23c;
}

.log-content {
  min-height: 460px;
  max-height: calc(100vh - 310px);
  margin: 0;
  padding: 14px;
  overflow: auto;
  color: #e8eaed;
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: #1f2937;
  border-radius: 4px;
}
</style>
